package com.arena_web.ui.components.modal

import androidx.compose.runtime.*
import com.arena_web.data.dto.BookingDto
import com.arena_web.data.dto.ClubClientCardDto
import com.arena_web.data.dto.ClubClientNoteDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.ModalFadeInKeyframes
import com.arena_web.ui.components.overlay.ModalOverlayFadeInKeyframes
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input

@Composable
fun ClientDetailPopup(
    card: ClubClientCardDto,
    bookings: List<BookingDto>,
    noteText: String,
    onNoteTextChanged: (String) -> Unit,
    onAddNote: () -> Unit,
    onSendMessage: () -> Unit,
    onClose: () -> Unit,
) {
    val t = LocalAppStrings.current
    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color.rgba(0, 0, 0, 0.7f))
            .onClick { onClose() }
            .animation(
                ModalOverlayFadeInKeyframes.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Popup card
        Column(
            modifier = Modifier
                .sWidth(600)
                .styleModifier {
                    property("max-height", "85vh")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
                .sBorderRadius(18)
                .sPadding(24)
                .sGap(20)
                .backgroundColor(Color.hex("09142D"))
                .onClick { it.stopPropagation() }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("box-shadow", "0 24px 48px rgba(0, 0, 0, 0.4)")
                }
        ) {
            // Contact + Metrics in one row
            ContactAndMetricsRow(card)

            // Action buttons centered
            var showNoteInput by remember { mutableStateOf(false) }
            ActionButtonsRow(
                onSendMessage = onSendMessage,
                onAddNoteClick = { showNoteInput = !showNoteInput }
            )

            // Admin Notes
            AdminNotesSection(
                notes = card.notes,
                noteText = noteText,
                onNoteTextChanged = onNoteTextChanged,
                onAddNote = {
                    onAddNote()
                    showNoteInput = false
                },
                showInput = showNoteInput
            )

            // Booking History
            BookingHistorySection(bookings)

            // Close button
            Box(
                Modifier
                    .fillMaxWidth()
                    .cursor(Cursor.Pointer)
                    .onClick { onClose() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.commonClose,
                    modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                        .styleModifier { property("opacity", "0.5") }
                )
            }
        }
    }
}

// =============================================================================
// Contact + Metrics Row (avatar, name/email, metrics pills — all in one row)
// =============================================================================

@Composable
private fun ContactAndMetricsRow(card: ClubClientCardDto) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth().sGap(16),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Avatar
        Div(attrs = {
            style {
                property("width", s(44))
                property("height", s(44))
                property("border-radius", s(100))
                property("overflow", "hidden")
                property("flex-shrink", "0")
                property("background", "#b4ccf6")
            }
        }) {
            if (card.avatarUrl != null) {
                Img(src = card.avatarUrl, alt = "", attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }
                })
            } else {
                Div(attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("font-size", s(16))
                        property("font-weight", "600")
                        property("color", "#02040b")
                        property("font-family", "Urbanist, sans-serif")
                    }
                }) {
                    val initials = (card.fullName ?: card.username)
                        .split(" ")
                        .take(2)
                        .mapNotNull { it.firstOrNull()?.uppercase() }
                        .joinToString("")
                    org.jetbrains.compose.web.dom.Text(initials)
                }
            }
        }

        // Name + Email
        Column(modifier = Modifier.sGap(4)) {
            SpanText(
                card.fullName ?: card.username,
                modifier = Modifier.h3().color(AppColors.Text.Secondary)
            )
            SpanText(
                card.email,
                modifier = Modifier.bodyMLight().color(BusinessColors.Text.OnSurfaceMuted)
            )
        }

        // Spacer
        Box(modifier = Modifier.styleModifier { property("flex", "1") })

        // Metric pills
        Row(modifier = Modifier.sGap(8)) {
            MetricPill(card.bookingCount.toString(), t.modalBookings)
            MetricPill("$ ${formatMoney(card.totalSpend)}", t.modalTotalSpent)
        }
    }
}

@Composable
private fun MetricPill(value: String, label: String) {
    Div(attrs = {
        style {
            property("border", "0.3px solid transparent")
            property("border-radius", s(100))
            property("padding", "${s(10)} ${s(18)}")
            property("background",
                "${AppGradients.InteractiveSecondary} padding-box, " +
                "linear-gradient(0deg, #0A142A, #0A142A) padding-box, " +
                "${AppGradients.ButtonBorderGradientPrimary} border-box")
            property("display", "flex")
            property("align-items", "center")
            property("gap", s(8))
            property("white-space", "nowrap")
        }
    }) {
        SpanText(
            value,
            modifier = Modifier.businessBodyXlSemiBold().color(AppColors.Primary)
        )
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
        )
    }
}

// =============================================================================
// Action Buttons Row (centered)
// =============================================================================

@Composable
private fun ActionButtonsRow(onSendMessage: () -> Unit, onAddNoteClick: () -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth().sGap(8),
        horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center
    ) {
        ActionPill(icon = AppIcons.Chat, label = t.modalSendMessage, onClick = onSendMessage)
        ActionPill(icon = AppIcons.Document, label = t.modalAddNote, onClick = onAddNoteClick)
    }
}

@Composable
private fun ActionPill(icon: String, label: String, onClick: () -> Unit) {
    var isHovered by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .sBorderRadius(100)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property("padding", "${s(10)} ${s(18)}")
                property("gap", s(8))
                property("background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                    "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
                if (isHovered) property("opacity", "0.8")
            }
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            src = icon,
            modifier = Modifier.sSize(16).styleModifier {
                property("filter", AppColors.IconDefaultFilter)
            }
        )
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
        )
    }
}

// =============================================================================
// Admin Notes Section
// =============================================================================

@Composable
private fun AdminNotesSection(
    notes: List<ClubClientNoteDto>,
    noteText: String,
    onNoteTextChanged: (String) -> Unit,
    onAddNote: () -> Unit,
    showInput: Boolean
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(14)
            .sPadding(16)
            .sGap(12)
            .styleModifier {
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("background", "rgba(0, 0, 0, 0.2)")
            }
    ) {
        // Header
        Row(
            modifier = Modifier.fillMaxWidth().sGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                src = AppIcons.Document,
                modifier = Modifier.sSize(18).styleModifier {
                    property("filter", AppColors.IconDefaultFilter)
                }
            )
            SpanText(
                t.modalAdminNotes,
                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
            )
            // Count badge
            if (notes.isNotEmpty()) {
                Box(
                    Modifier
                        .sBorderRadius(100)
                        .sPadding(2, 8)
                        .backgroundColor(AppColors.Primary),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        notes.size.toString(),
                        modifier = Modifier.bodySMedium().color(AppColors.PrimaryWhite)
                    )
                }
            }

            // Spacer
            Box(modifier = Modifier.styleModifier { property("flex", "1") })

            // Subtitle — right-aligned
            SpanText(
                t.modalAdminNotesPrivacy,
                modifier = Modifier.bodySRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    .styleModifier { property("opacity", "0.6") }
            )
        }

        // Note input (shown when "Add Note" button is clicked)
        if (showInput) {
            Row(
                modifier = Modifier.fillMaxWidth().sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("display", "flex")
                        property("align-items", "center")
                        property("border-radius", s(22))
                        property("height", s(36))
                        property("padding", "0 ${s(12)}")
                        property("background", "rgba(255, 255, 255, 0.05)")
                        property("border", "0.5px solid rgba(255, 255, 255, 0.1)")
                    }
                }) {
                    Input(type = InputType.Text) {
                        value(noteText)
                        onInput { onNoteTextChanged(it.value) }
                        attr("placeholder", t.modalWriteNotePlaceholder)
                        style {
                            property("flex", "1")
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("color", "#bed6f5")
                            property("font-size", s(14))
                            property("font-family", "Urbanist, sans-serif")
                            property("font-weight", "400")
                        }
                    }
                }

                // Add button
                Box(
                    Modifier
                        .sBorderRadius(22)
                        .sHeight(36)
                        .sPadding(0, 16)
                        .backgroundColor(AppColors.Primary)
                        .cursor(Cursor.Pointer)
                        .onClick { onAddNote() }
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", s(6))
                            property("opacity", if (noteText.isBlank()) "0.5" else "1")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.modalAdd,
                        modifier = Modifier.bodyMRegular().color(AppColors.PrimaryWhite)
                    )
                }
            }
        }

        // Notes list
        if (notes.isNotEmpty()) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(8)
                    .styleModifier {
                        property("max-height", s(200))
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                    }
            ) {
                notes.sortedByDescending { it.createdAt }.forEach { note ->
                    NoteItem(note)
                }
            }
        }
    }
}

@Composable
private fun NoteItem(note: ClubClientNoteDto) {
    Row(
        modifier = Modifier.fillMaxWidth().sGap(8).sPadding(8, 0),
        verticalAlignment = Alignment.Top
    ) {
        Icon(
            src = AppIcons.Document,
            modifier = Modifier.sSize(14).styleModifier {
                property("filter", AppColors.IconDefaultFilter)
                property("opacity", "0.5")
                property("flex-shrink", "0")
                property("margin-top", s(2))
            }
        )
        Column(modifier = Modifier.sGap(4).styleModifier { property("flex", "1") }) {
            SpanText(
                note.text,
                modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
            )
            SpanText(
                formatNoteDate(note.createdAt),
                modifier = Modifier.bodySRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    .styleModifier { property("opacity", "0.6") }
            )
        }
    }
}

// =============================================================================
// Booking History Section
// =============================================================================

@Composable
private fun BookingHistorySection(bookings: List<BookingDto>) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(14)
            .sPadding(16)
            .sGap(12)
            .styleModifier {
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("background", "rgba(0, 0, 0, 0.2)")
            }
    ) {
        // Header
        Row(
            modifier = Modifier.fillMaxWidth().sGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                src = AppIcons.Calendar,
                modifier = Modifier.sSize(18).styleModifier {
                    property("filter", AppColors.IconDefaultFilter)
                }
            )
            SpanText(
                t.modalBookingHistory,
                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
            )
            if (bookings.isNotEmpty()) {
                Box(
                    Modifier
                        .sBorderRadius(100)
                        .sPadding(2, 8)
                        .backgroundColor(AppColors.Primary),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        bookings.size.toString(),
                        modifier = Modifier.bodySMedium().color(AppColors.PrimaryWhite)
                    )
                }
            }
        }

        // Bookings list
        if (bookings.isEmpty()) {
            SpanText(
                t.modalNoBookingsYet,
                modifier = Modifier.bodyMRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    .styleModifier { property("opacity", "0.6") }
            )
        } else {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(0)
                    .styleModifier {
                        property("max-height", s(300))
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                    }
            ) {
                bookings.forEach { booking ->
                    BookingItem(booking)
                }
            }
        }
    }
}

@Composable
private fun BookingItem(booking: BookingDto) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(10, 0)
            .styleModifier {
                property("border-bottom", "1px solid rgba(148, 149, 152, 0.15)")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Date
        SpanText(
            formatBookingDate(booking.bookingDate),
            modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                .sWidth(90)
        )

        // Field name with dot
        Row(
            modifier = Modifier.sGap(6).styleModifier { property("flex", "1") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Blue dot
            Div(attrs = {
                style {
                    property("width", s(6))
                    property("height", s(6))
                    property("border-radius", s(100))
                    property("background", "#0061ff")
                    property("flex-shrink", "0")
                }
            })
            SpanText(
                booking.fieldName ?: t.modalFieldFallback,
                modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
            )
        }

        // Time
        Row(
            modifier = Modifier.sGap(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                src = AppIcons.Time,
                modifier = Modifier.sSize(12).styleModifier {
                    property("filter", AppColors.IconDefaultFilter)
                    property("opacity", "0.5")
                }
            )
            SpanText(
                "${booking.startTime} - ${booking.endTime}",
                modifier = Modifier.bodySRegular().color(BusinessColors.Text.OnSurfaceMuted)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }

        // Status + Payment badges
        Box(modifier = Modifier.sWidth(8))
        BookingStatusBadge(booking.status)
        Box(modifier = Modifier.sWidth(4))
        PaymentBadge(booking.isPaid)

        // Price
        Box(modifier = Modifier.sWidth(8))
        SpanText(
            "$ ${formatMoney(booking.totalPrice)}",
            modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun BookingStatusBadge(status: String) {
    val t = LocalAppStrings.current
    val (color, text) = when (status.uppercase()) {
        "CONFIRMED" -> Color.hex("7BFA6F") to t.modalStatusConfirmed
        "PENDING" -> Color.hex("FFA438") to t.modalStatusPending
        "COMPLETED" -> Color.hex("7BFA6F") to t.modalStatusCompleted
        "CANCELLED" -> Color.hex("FF4242") to t.modalStatusCancelled
        else -> BusinessColors.Text.OnSurfaceMuted to status
    }

    Box(
        Modifier
            .sBorderRadius(100)
            .sPadding(4, 10)
            .styleModifier {
                property("background", "rgba(255, 255, 255, 0.05)")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier.bodySMedium().color(color)
        )
    }
}

@Composable
private fun PaymentBadge(isPaid: Boolean) {
    val t = LocalAppStrings.current
    val color = if (isPaid) Color.hex("3CFFFF") else Color.hex("FF4242")
    val text = if (isPaid) t.modalPaid else t.modalUnpaid

    Box(
        Modifier
            .sBorderRadius(100)
            .sPadding(4, 10)
            .styleModifier {
                property("background", "rgba(255, 255, 255, 0.05)")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text,
            modifier = Modifier.bodySMedium().color(color)
        )
    }
}

// =============================================================================
// Helpers
// =============================================================================

private fun formatMoney(value: String): String {
    return try {
        val num = value.toDouble()
        val intPart = num.toLong()
        val formatted = intPart.toString().reversed().chunked(3).joinToString(",").reversed()
        if (num != intPart.toDouble()) {
            val decimal = ((num - intPart) * 100).toLong()
            "$formatted.$decimal"
        } else {
            formatted
        }
    } catch (_: Exception) {
        value
    }
}

private fun formatNoteDate(dateStr: String): String {
    return try {
        // Expected format: ISO datetime string, take date part
        val datePart = dateStr.substringBefore("T")
        val parts = datePart.split("-")
        if (parts.size == 3) {
            "${parts[2]}.${parts[1]}.${parts[0]}"
        } else dateStr
    } catch (_: Exception) {
        dateStr
    }
}

private fun formatBookingDate(dateStr: String): String {
    return try {
        val parts = dateStr.split("-")
        if (parts.size == 3) {
            val months = listOf("", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
            val month = parts[1].toInt()
            val day = parts[2].toInt()
            "${months.getOrElse(month) { "?" }} $day"
        } else dateStr
    } catch (_: Exception) {
        dateStr
    }
}
